import { Box, Flex } from "@chakra-ui/layout";
import { Text } from "@chakra-ui/react";
import { Row, Col } from "antd";
import { Control, UseFormSetValue } from "react-hook-form";
import { TransferenciaForm } from "core/features/transferencia/typings";
import Field from "ui/components/Input/Field";
import SelectContaBancaria from "ui/components/Input/SelectContaBancaria";
import InputMoney from "ui/components/Input/InputMoney";
import SelectContaCategoria from "ui/components/Input/SelectContaCategoria";
import colors from "core/resources/theme/colors";
import { HiOutlineArrowDown } from "react-icons/hi";
import ButtonAnexarArquivos from "components/Button/ButtonAnexarArquivos";
import Anexos from "components/Anexos";
import { OperacaoEnum } from "resources/enums/conta";
import useUploadAnexo from "core/features/arquivo/useUploadAnexo";
import { useState } from "react";

function FormTransferencia({
  control,
  form,
  setValue,
}: {
  control: Control<TransferenciaForm, object>;
  form: TransferenciaForm;
  setValue: UseFormSetValue<TransferenciaForm>;
}) {
  const [disableButtonAnexar, setDisabledButtonAnexar] = useState(false);

  return (
    <form>
      <Text as="h3" fontSize={20} fontWeight="500">
        Nova transferência
      </Text>
      <Box as={Row} gutter={16} mb={10}>
        <Box as={Col} xs={12}>
          <Field
            label="Data"
            control={control}
            name="data"
            rules={{
              required: "Campo requerido",
            }}
            inputProps={{
              type: "date",
            }}
          />
        </Box>
        <Box as={Col} xs={12}>
          <Field
            label="Valor"
            control={control}
            name="valor"
            rules={{
              required: "Campo requerido",
            }}
            render={({ field, isInvalid }) => (
              <InputMoney
                value={field.value}
                onChange={field.onChange}
                isInvalid={isInvalid}
              />
            )}
          />
        </Box>
      </Box>
      <Box bg={colors.blue100} p={4} rounded={6} mb={4}>
        <Text fontSize="18px" fontWeight="bold">
          De
        </Text>
        <Box as={Row} gutter={16} mt={2}>
          <Box as={Col} xs={12}>
            <Field
              label="Conta bancária"
              control={control}
              name="contaOrigemId"
              rules={{
                required: "Campo requerido",
              }}
              render={({ field, isInvalid }) => (
                <SelectContaBancaria
                  bg="white"
                  value={field.value}
                  onChange={field.onChange}
                  isInvalid={isInvalid}
                  incluirInativas={false}
                />
              )}
            />
          </Box>

          <Box as={Col} xs={12}>
            <Field
              control={control}
              label="Conta categoria"
              name="categoriaTransferenciaEnvioId"
              rules={{
                required: "Campo requerido",
              }}
              inputProps={{ background: "none !important" }}
              render={({ field, isInvalid }) => (
                <SelectContaCategoria
                  disableNotClassifiableNodes
                  bg="white"
                  placeholder="Selecione"
                  size="large"
                  isInvalid={isInvalid}
                  operacao={OperacaoEnum.Despesa}
                  {...field}
                />
              )}
            />
          </Box>
        </Box>
      </Box>

      <Box as={Row} gutter={16} mb={6} role="separator">
        <Flex as={Col} xs={11} align="center">
          <Box h={0.5} bg={colors.blue200} w="100%" />
        </Flex>
        <Flex as={Col} xs={2}>
          <HiOutlineArrowDown size={25} color={colors.blue200} />
        </Flex>
        <Flex as={Col} xs={11} align="center">
          <Box h={0.5} bg={colors.blue200} w="100%" />
        </Flex>
      </Box>

      <Box bg={colors.blue100} p={4} rounded={6} mb={4}>
        <Text fontSize="18px" fontWeight="bold">
          Para
        </Text>
        <Box as={Row} gutter={16} mt={2}>
          <Box as={Col} xs={12}>
            <Field
              label="Conta bancária"
              control={control}
              name="contaDestinoId"
              rules={{
                required: "Campo requerido",
              }}
              render={({ field, isInvalid }) => (
                <SelectContaBancaria
                  bg="white"
                  value={field.value}
                  onChange={field.onChange}
                  isInvalid={isInvalid}
                  filteredIds={form.contaOrigemId ? [form.contaOrigemId] : []}
                  incluirInativas={false}
                />
              )}
            />
          </Box>
          <Box as={Col} xs={12}>
            <Field
              control={control}
              label="Conta categoria"
              name="categoriaTransferenciaRecebimentoId"
              rules={{
                required: "Campo requerido",
              }}
              render={({ field, isInvalid }) => (
                <SelectContaCategoria
                  disableNotClassifiableNodes
                  bg="white"
                  placeholder="Selecione"
                  size="large"
                  isInvalid={isInvalid}
                  operacao={OperacaoEnum.Receita}
                  {...field}
                />
              )}
            />
          </Box>
        </Box>
      </Box>

      <Flex mt={8} flexDir="column">
        <Text as="h3" mb={2} fontWeight="500" fontSize={18}>
          Arquivos
        </Text>
        <Box as={Row}>
          <Field
            control={control}
            name="anexos"
            render={() => (
              <Box flex={1}>
                <ButtonAnexarArquivos
                  disabled={disableButtonAnexar}
                  accept="image/*, application/pdf, video/*, .csv, .xlsx"
                  maxFileSizeMb={200}
                  currentFilesLength={form?.anexos?.length || 0}
                  variant="ghost"
                  onAnexar={(files) => {
                    if (files) {
                      const currentAnexos = form?.anexos;

                      setValue(
                        "anexos",
                        currentAnexos ? [...currentAnexos, ...files] : files
                      );
                    }
                  }}
                />
                <Flex
                  gap="16px"
                  mt="auto"
                  h="fit-content"
                  px={4}
                  py={2}
                  flexDir="column"
                >
                  {form.anexos?.length ? (
                    <Anexos
                      onStartUploadMutation={() =>
                        setDisabledButtonAnexar(true)
                      }
                      onFinishUploadMutation={() =>
                        setDisabledButtonAnexar(false)
                      }
                      numColumns={2}
                      autoUploadMutation={useUploadAnexo}
                      selectTipoAnexoConta
                      setValue={(anexos) => {
                        setValue(
                          "anexos",
                          anexos?.map((anexo) =>
                            Object.assign(anexo, {
                              nome:
                                anexo instanceof File ||
                                Reflect.has(anexo, "name")
                                  ? anexo.name
                                  : anexo.nome!,
                            })
                          ) || []
                        );
                      }}
                      anexos={
                        form.anexos?.map((file) =>
                          file instanceof File
                            ? file
                            : { ...file, name: file.nome ?? file.name }
                        ) || undefined
                      }
                    />
                  ) : null}
                </Flex>
              </Box>
            )}
          />
        </Box>
      </Flex>
    </form>
  );
}

export default FormTransferencia;
